import {DispatchTrackingRoute, RouteGeometry} from "@ots-frontend/ots-data";
import {decode} from "@googlemaps/polyline-codec";
import {useMemo} from "react";
import {useApp} from "../../app/AppContext";
import {OtsGoogleMap, OtsMapMarker, OtsMapPath, OtsMapPosition, otsMapPosition} from "../../components/OtsGoogleMap";
import {DriverTracking, humanizeTrackingCode} from "./driver-tracking";

export type DriverTrackingMapProps = {
  tracking?: DriverTracking;
  /** Falls back to the host app's Google Maps key. */
  apiKey?: string;
  className?: string;
};

// Google draws markers itself, so these are plain colors rather than theme variables
const markerColors = {
  origin: "#94a3b8",
  driver: "#2563eb",
  pickup: "#16a34a",
  delivery: "#dc2626",
};

export const routePath = (geometry?: RouteGeometry): OtsMapPosition[] => {
  if (geometry?.coordinates?.length) return geometry.coordinates.map(point => ({lat: point.lat, lng: point.lon}));
  if (geometry?.encodedPolyline) return decode(geometry.encodedPolyline, 5).map(([lat, lng]) => ({lat, lng}));
  return [];
};

/** Builds the markers and route line for a driver on the way to a pickup or delivery. */
export const getDriverTrackingMapContent = (tracking?: DriverTracking): {markers: OtsMapMarker[]; paths: OtsMapPath[]} => {
  const markers: OtsMapMarker[] = [];
  const origin = tracking?.performer?.locationContext?.meta?.origin;
  const location = tracking?.performer?.locationContext?.location;
  const destination = tracking?.destination;

  const originPosition = otsMapPosition(origin?.latitude, origin?.longitude);
  if (originPosition) {
    markers.push({
      id: "origin",
      position: originPosition,
      label: `Coming from ${origin?.siteName ?? humanizeTrackingCode(origin?.source) ?? "start"}`,
      color: markerColors.origin,
      size: 7,
    });
  }

  const driverPosition = otsMapPosition(location?.latitude, location?.longitude);
  if (driverPosition) {
    markers.push({
      id: "driver",
      position: driverPosition,
      label: tracking?.performer?.personName ?? "Driver",
      color: markerColors.driver,
      size: 11,
    });
  }

  const destinationPosition = otsMapPosition(destination?.latitude, destination?.longitude);
  if (destinationPosition) {
    const delivery = destination?.type === "DELIVERY";
    markers.push({
      id: "destination",
      position: destinationPosition,
      label: `${delivery ? "Delivery" : "Pickup"}: ${destination?.siteName ?? "destination"}`,
      color: delivery ? markerColors.delivery : markerColors.pickup,
      text: delivery ? "D" : "P",
    });
  }

  const path = routePath((tracking?.route as DispatchTrackingRoute | undefined)?.geometry);
  return {
    markers,
    paths: path.length >= 2 ? [{id: "route", path, color: markerColors.driver, weight: 4}] : [],
  };
};

/** Satellite map of where the driver came from, where they are, and where they're headed. */
export const DriverTrackingMap = ({tracking, apiKey, className}: DriverTrackingMapProps) => {
  const app = useApp();
  const content = useMemo(() => getDriverTrackingMapContent(tracking), [tracking]);

  return (
    <OtsGoogleMap
      apiKey={apiKey ?? app?.googleMapsApiKey}
      markers={content.markers}
      paths={content.paths}
      subject="the driver"
      emptyMessage="No driver or destination location to map yet."
      className={["h-60", className].filter(Boolean).join(" ")}/>
  );
};
